import { RefObject, useEffect, useRef } from 'react';

const SWIPE_THRESHOLD = 5; //px

export const useBlockVerticalScrollOnHorizontalSwipe = (
  ref: RefObject<HTMLDivElement | null>,
  enabled: boolean,
) => {
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const directionRef = useRef<'horizontal' | 'vertical' | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const element = ref.current;
    if (!element) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
      directionRef.current = null;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStartRef.current) return;

      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);

      if (directionRef.current === null && (deltaX > SWIPE_THRESHOLD || deltaY > SWIPE_THRESHOLD)) {
        directionRef.current = deltaX > deltaY ? 'horizontal' : 'vertical';
      }

      if (directionRef.current === 'horizontal') {
        e.preventDefault();
      } else if (directionRef.current === 'vertical') {
        e.stopPropagation();
      }
    };

    const handleTouchEnd = () => {
      touchStartRef.current = null;
      directionRef.current = null;
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: true, capture: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true, capture: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart, { capture: true });
      element.removeEventListener('touchmove', handleTouchMove, { capture: true });
      element.removeEventListener('touchend', handleTouchEnd, { capture: true });
    };
  }, [ref, enabled]);
};
